// hooks
import { Fragment, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useHttp from "hooks/use-http";

// routes
import { useParams, useNavigate } from "react-router-dom";

// material styles
import { Grid, TextField, Button } from "@mui/material";

// material icons
import { ReportGmailerrorred, Check } from "@mui/icons-material/";

// request methods
import {
  addTeacherClassScoreRequest,
  getStudentsListRequest,
} from "api/requests";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import BackButton from "components/shared/BackButton";
import DatePickerCM from "components/shared/DatePicker";

// helper
import Toast from "helper/toast";
import { convertNum } from "helper/convert-num";

// styled component
import {
  StyledStack,
  StyledTitle,
  StyledDateBox,
  StyledStudentTitle,
  StyledStudentName,
  ButtonContainer,
} from "./AddScore.style";
import CheckBoxBtn from "components/Score/CheckBox";
import { AddScoreSchema } from "validations";
import CheckBoxWithField from "components/Score/CheckBox";

export default function AddTeacherClassScore() {
  const { class_id } = useParams();

  const navigate = useNavigate();

  const { request, loading } = useHttp();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(AddScoreSchema),
  });

  // class name
  const [className] = useState("");

  const [studentsData, setStudentsData] = useState({
    data: [{ content: "دانش اموزی یافت نشد" }],
    count: 0,
  });

  // date and time state
  const [date, setDate] = useState("");

  // date and time handler
  const onDateTimeHandler = (value) =>
    setDate(
      value
        .format("YYYY-MM-DD")
        .split("-")
        .map((item) => convertNum(item))
        .join("-")
    );

  // add class score
  const onSubmit = async (data) => {
    const students = [];
    const scores = Object.entries(data.students.score);
    Object.entries(data.students.is_present).map((item, index) => {
      return !item[1] && scores[index][1] !== ""
        ? students.push({
            student_id: item[0].toString(),
            score: scores[index][1],
            is_present: !item[1],
          })
        : "";
    });
    if (students.length < 1) {
      return Toast({
        type: "custom-err",
        title: "برای هیچ دانش آموزی نمره ثبت نشده",
      });
    }
    const res = await request({
      url: () =>
        addTeacherClassScoreRequest({
          ...data,
          class_id: class_id,
          date: date,
          students,
        }),
    });
    if (res) {
      Toast({ type: "success", title: "ثبت نمرات " });
      reset({
        date: "",
        class_course_id: "",
        students: "",
      });
      navigate(-1);
    }
  };

  // // get class score data
  const getClassStudnetsData = useCallback(async () => {
    const res = await request({
      url: getStudentsListRequest,
      values: {
        skip: 0,
        limit: 100,
        class_id,
      },
    });

    if (!res.count) {
      setStudentsData({
        data: [{ content: "دانش اموزی یافت نشد" }],
        count: 0,
      });
    } else {
      const myList = res.data;
      const collator = new Intl.Collator("fa");
      const sortedLetters = myList.sort((a, b) =>
        collator.compare(a.last_name, b.last_name)
      );
      const transformedData = sortedLetters.map((item) => ({
        id: item.id,
        first_name: item.first_name,
        last_name: item.last_name,
      }));

      setStudentsData({
        data: transformedData,
        count: res.count,
      });
    }
  }, []);

  useEffect(() => getClassStudnetsData(), [getClassStudnetsData]);

  return (
    <DrawerCM title="افزودن نمرات کلاسی" $styledHeader>
      {loading ? (
        <LoadingCM />
      ) : (
        <Fragment>
          <Grid container>
            <Grid item mt={3} xs={12}>
              <BackButton Route={`/classes/${class_id}/scores`} />
            </Grid>
            <StyledStack>
              <Grid>
                <StyledTitle>{`افزودن نمرات کلاس ${className}`}</StyledTitle>
                <Grid container>
                  <Fragment>
                    {studentsData.count < 1 ? (
                      <Grid item xs={12} p={1.5}>
                        <Button
                          color="error"
                          startIcon={<ReportGmailerrorred />}
                        >
                          برای این کلاس دانش آموزی ثبت نشده
                        </Button>
                      </Grid>
                    ) : (
                      <Grid container>
                        <Grid item sm={5} xs={12} p={1.5}>
                          <StyledDateBox>
                            <DatePickerCM
                              onChange={onDateTimeHandler}
                              render={(value, openCalendar) => {
                                return (
                                  <TextField
                                    label="تاریخ"
                                    variant="outlined"
                                    id="date"
                                    {...register("date")}
                                    error={Boolean(errors.date)}
                                    helperText={errors.date?.message}
                                    value={value ? value : ""}
                                    onClick={openCalendar}
                                  />
                                );
                              }}
                            />
                          </StyledDateBox>
                        </Grid>
                        <Grid item xs={12} p={1.5}>
                          <StyledStudentTitle>
                            نام و نام خانوادگی دانش آموز:
                          </StyledStudentTitle>
                        </Grid>
                        {studentsData.data?.map((item, index) => (
                          <Fragment key={index}>
                            <Grid item sm={6} xs={12} p={1.5}>
                              <StyledStudentName>{`${item.first_name} ${item.last_name}`}</StyledStudentName>
                            </Grid>
                            <CheckBoxWithField
                              id={item.id}
                              registerField={{
                                ...register(`students.score[${item.id}]`),
                              }}
                              register={{
                                ...register(`students.is_present[${item.id}]`),
                              }}
                            ></CheckBoxWithField>
                          </Fragment>
                        ))}
                      </Grid>
                    )}
                  </Fragment>
                </Grid>
              </Grid>

              <ButtonContainer>
                <Button
                  color="success"
                  startIcon={<Check />}
                  variant="contained"
                  sx={{ mt: 1, mr: 1 }}
                  onClick={handleSubmit(onSubmit)}
                >
                  ثبت
                </Button>
              </ButtonContainer>
            </StyledStack>
          </Grid>
        </Fragment>
      )}
    </DrawerCM>
  );
}
